import React, { useEffect, useState } from 'react';
import { cardService } from '../../services/cardService';
import { paymentService } from '../../services/payment';
import { useAuth } from '../../hooks/useAuth';
import { Student, RFIDCard } from '../../types';
import { useSearchParams, useNavigate } from 'react-router-dom';

const PAYFAST_URL = "https://ipg1.apps.net.pk/Ecommerce/api/Transaction/PostTransaction";

const CardTopUp: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const basketIdFromUrl =
    searchParams.get('basketId') ||
    searchParams.get('basket_id');
  const statusFromUrl = searchParams.get('status');
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [studentCards, setStudentCards] = useState<RFIDCard[]>([]);
  const [amount, setAmount] = useState<string>('');
  const [currentBasketId, setCurrentBasketId] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [message, setMessage] = useState('');

  const MIN_AMOUNT = 500;
  const MAX_AMOUNT = 5000;

  // Clear URL basketId when component mounts (if it's a new session)
    useEffect(() => {
      if (!basketIdFromUrl) return;

      const processedKey = `payment_done_${basketIdFromUrl}`;

      if (sessionStorage.getItem(processedKey)) return;

      let isActive = true;

      const verifyPayment = async () => {
        try {
          setLoading(true);

          // 👇 ADD THIS CHECK HERE (before API call UI state)
          if (statusFromUrl === 'success') {
            setStatus('processing'); // still verify backend
            setMessage('Verifying payment...');
          } else if (statusFromUrl === 'failed') {
            setStatus('failed');
            setMessage('Payment failed. Please try again.');
            return;
          } else {
            setStatus('processing');
            setMessage('Verifying payment...');
          }

          const res = await paymentService.getPaymentStatus(basketIdFromUrl);

          if (!isActive) return;

          if (res?.status === 'success') {
            sessionStorage.setItem(processedKey, '1');

            setStatus('success');
            setMessage('Payment successful! Redirecting...');

            setTimeout(() => {
              navigate('/', { replace: true }); // ✅ DASHBOARD
            }, 2000);

            return;
          }

          sessionStorage.setItem(processedKey, '1');

          setStatus('failed');
          setMessage('Payment failed. Please try again.');

        } catch (err) {
          if (!isActive) return;

          setStatus('failed');
          setMessage('Unable to verify payment.');
        } finally {
          if (isActive) setLoading(false);
        }
      };

      verifyPayment();

      return () => {
        isActive = false;
      };
    }, [basketIdFromUrl, statusFromUrl, navigate]);
  // Load students
  useEffect(() => {
    const load = async () => {
      const data = await cardService.getUserStudents();
      setStudents(data);
      if (data.length > 0) {
        setSelectedStudent(data[0].id.toString());
      }
    };
    load();
  }, []);

  // Load cards
  useEffect(() => {
    const loadCards = async () => {
      if (!selectedStudent) return;
      const cards = await cardService.getStudentCards(parseInt(selectedStudent));
      setStudentCards(cards);
    };
    loadCards();
  }, [selectedStudent]);

  const selectedStudentData = students.find(s => s.id.toString() === selectedStudent);
  const activeCard = studentCards.find(c => c.isActive && !c.isLost);

 
  const validateAmount = () => {
    const num = parseFloat(amount);
    if (!num || isNaN(num)) {
      setMessage('Enter a valid amount');
      return false;
    }
    if (num < MIN_AMOUNT) {
      setMessage(`Minimum amount is PKR ${MIN_AMOUNT}`);
      return false;
    }
    if (num > MAX_AMOUNT) {
      setMessage(`Maximum amount is PKR ${MAX_AMOUNT}`);
      return false;
    }
    return true;
  };

  const handleQuickAmount = (val: number) => {
    setAmount(val.toString());
    setMessage('');
  };

  const handleSubmit = async () => {
    setMessage('');

    if (!validateAmount()) return;
    if (!activeCard) {
      setMessage('No active card found');
      return;
    }

    setLoading(true);

    try {
      console.log('Initiating payment for amount:', amount);
      
      const res = await paymentService.initiateCardPayment({
        studentId: parseInt(selectedStudent),
        cardId: activeCard.id,
        amount: parseFloat(amount)
      });
      
      console.log('Initiate response:', res);
      console.log('Generated Token:', res.token);
      console.log('Basket ID:', res.basketId);
      
      // Store current basketId
      setCurrentBasketId(res.basketId);
      
      // Create PayFast form
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = PAYFAST_URL;

      const fields = {
        CURRENCY_CODE: "PKR",
        MERCHANT_ID: res.merchantId,
        MERCHANT_NAME: 'RFID System',
        TOKEN: res.token,
        PROCCODE: '00',
        TRAN_TYPE: 'ECOMM_PURCHASE',
        TXNAMT: res.amount.toFixed(2),
        CUSTOMER_EMAIL_ADDRESS: user?.email || 'test@test.com',
        CUSTOMER_MOBILE_NO: user?.phone || '03000000000',
        SIGNATURE: res.signature,
        VERSION: 'MERCHANTCART-1.0',
        TXNDESC: 'Card Topup',
        SUCCESS_URL: `${window.location.origin}/#/topup/card?basketId=${res.basketId}&status=success`,
        FAILURE_URL: `${window.location.origin}/#/topup/card?basketId=${res.basketId}&status=failed`,
        BASKET_ID: res.basketId,
        ORDER_DATE: new Date().toISOString().slice(0, 19).replace('T', ' '),
        CHECKOUT_URL: `https://raastqr.vendingc.com/api/card-payment/ipn`,
        MERCHANT_USERAGENT: navigator.userAgent
      };
      
      // Log the form data being submitted
      console.log('Submitting form with data:', fields);
      
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      console.log('=== SUBMITTING TO PAYFAST ===');
      console.log('Form Action:', PAYFAST_URL);
      console.log('Form Data:');
      Object.entries(fields).forEach(([key, value]) => {
      console.log(`  ${key}: ${value}`);
      });

      // Verify token is not empty
      if (!res.token || res.token.length === 0) {
      console.error('ERROR: Token is empty!');
      setMessage('Payment configuration error. Please try again.');
      setLoading(false);
      return;
      }

      console.log(`Token being sent: ${res.token}`);
      console.log(`Token length: ${res.token.length}`);
      form.submit();

    } catch (err: any) {
      console.error('Payment initiation error:', err);
      setMessage(err.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  return (
    
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Top-Up Card
          </h1>

          <p className="text-sm text-gray-500 mt-0">
            Secure online payment using Debit or Credit Card
          </p>
        </div>

        <div className="hidden md:flex items-center px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
          <svg
            className="w-5 h-5 text-blue-600 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 1.343-3 3m6 0a3 3 0 00-3-3m0 0V5m0 6v6m0 0h3m-3 0H9"
            />
          </svg>

          <span className="text-sm font-medium text-blue-700">
            PayFast Secure Gateway
          </span>
        </div>
      </div>
      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-4 md:p-6">
        {/* Only show form if no basketId in URL or status is idle/failed */}
        {(!basketIdFromUrl || status === 'failed') && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT SIDE - Form Fields */}
            <div className="space-y-6">
              <div className="mb-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 ">
                  Before you continue
                </h3>

                <ul className="text-xs text-blue-700 space-y-2 list-disc pl-5">
                  <li>Ensure stable internet connection during payment</li>
                  <li>Do not close or refresh browser after clicking payment button</li>
                  <li>Keep your bank OTP / SMS device ready</li>
                  <li>Wait for automatic verification after payment completion</li>
                  <li>Do not press back button during gateway processing</li>
                </ul>
              </div>
              <div>
                  {/* STATUS */}
                  {status !== 'idle' && (
                    <div className="mb-2">

                      {status === 'processing' && (
                        <div className="flex items-center p-4 rounded-lg bg-blue-50 border border-blue-100">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                          <div>
                            <p className="text-sm font-medium text-blue-800">
                              Processing Payment
                            </p>
                            <p className="text-xs text-blue-600">
                              {message}
                            </p>
                          </div>
                        </div>
                      )}

                      {status === 'success' && (
                        <div className="flex items-center p-4 rounded-lg bg-green-50 border border-green-100">
                          <div className="mr-3 text-green-600 text-xl">✓</div>
                          <div>
                            <p className="text-sm font-medium text-green-800">
                              Payment Successful
                            </p>
                            <p className="text-xs text-green-600">
                              {message}
                            </p>
                          </div>
                        </div>
                      )}

                      {status === 'failed' && (
                        <div className="flex items-center p-4 rounded-lg bg-red-50 border border-red-100">
                          <div className="mr-3 text-red-600 text-xl">✕</div>
                          <div>
                            <p className="text-sm font-medium text-red-800">
                              Payment Failed
                            </p>
                            <p className="text-xs text-red-600">
                              {message}
                            </p>
                          </div>
                        </div>
                      )}

                    </div>
                  )}
                <label className="block text-sm font-medium text-gray-700 mb-2">Select User</label>
                <select
                  value={selectedStudent}
                  onChange={e => setSelectedStudent(e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 border disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {students.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.studentId})
                    </option>
                  ))}
                </select>
              </div>

             {selectedStudentData && (
                <div className="mt-4 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-blue-900">
                      Current Balance:
                    </span>
                    <span className="text-lg font-semibold text-green-600">
                      PKR {selectedStudentData.balance.toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  
                  {activeCard && (
                    <div className="mt-1">
                      <p className="text-sm text-gray-600">
                        Current Card: {activeCard.cardNumber}
                      </p>
                    </div>
                  )}
                  
                  {!activeCard && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-xs text-red-700 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        No active card. Please add a card in Card Management.
                      </p>
                    </div>
                  )}
                </div>
              )}
             <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Amount (PKR)
                </label>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">PKR</span>
                  </div>

                  <input
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    disabled={loading}
                    className="pl-12 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md p-3 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="0.00"
                  />
                </div>

                <div className="mt-2 flex justify-between text-sm text-gray-500">
                  <span>Min: PKR {MIN_AMOUNT}</span>
                  <span>Max: PKR {MAX_AMOUNT}</span>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Quick Select</p>
                <div className="grid grid-cols-4 gap-2">
                  {[500, 1000, 2000,500].map(v => (
                    <button
                      key={v}
                      onClick={() => handleQuickAmount(v)}
                      className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      disabled={loading}
                    >
                      PKR {v}
                    </button>
                  ))}
                </div>
              </div>

             {message && status !== 'processing' && (
                <div className={`p-3 rounded-md border text-sm ${
                  status === 'success'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : status === 'failed'
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-orange-50 border-yellow-200 text-red-700'
                }`}>
                  {message}
                </div>
              )}
              <div className="bg-gray-50 rounded-md border p-4">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">
                    Top-Up Amount
                  </span>

                  <span className="text-lg font-semibold text-blue-600">
                    PKR {amount || '0'}
                  </span>
                </div>
              </div>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? 'Processing...' : 'Pay with Debit/Credit Card'}
              </button>
            </div>

            {/* RIGHT SIDE - Info */}
           <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-6 h-full">

            <div className="text-center">

              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                <svg
                  className="h-8 w-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Secure Card Payment
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                You will be redirected to PayFast secure payment gateway to complete your transaction safely.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md mb-4 text-left">
                <p className="text-sm text-blue-700">
                  <strong>Supported Cards:</strong><br />
                  Visa • Mastercard • Debit Cards • Credit Cards
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md text-left">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">
                  Payment Tips
                </h4>

                <ul className="text-sm text-yellow-700 space-y-1 list-disc pl-5">
                  <li>Ensure stable internet connection</li>
                  <li>Do not refresh during payment</li>
                  <li>Wait for automatic verification after payment</li>
                  <li>Keep OTP/SMS device nearby</li>
                </ul>
              </div>

              <div className="mt-6 flex items-center justify-center text-sm text-green-600">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2V7a3 3 0 00-6 0v2h6z"
                    clipRule="evenodd"
                  />
                </svg>

                Secure & Encrypted Payment
              </div>

            </div>
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardTopUp;